import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';

// Password validation schema with strength requirements
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must not exceed 128 characters')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^a-zA-Z0-9]/, 'Password must contain at least one special character');

// Email validation schema
export const emailSchema = z
  .string()
  .email('Please enter a valid email address')
  .max(254, 'Email address is too long')
  .refine(
    (email) => {
      // Basic email pattern validation
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailPattern.test(email);
    },
    'Please enter a valid email address'
  );

// Display name validation schema
export const displayNameSchema = z
  .string()
  .min(1, 'Display name is required')
  .max(50, 'Display name must not exceed 50 characters')
  .refine(
    (name) => {
      // Allow letters, numbers, spaces, hyphens, apostrophes, and periods
      const namePattern = /^[a-zA-Z0-9\s\-'.]+$/;
      return namePattern.test(name.trim());
    },
    'Display name can only contain letters, numbers, spaces, hyphens, apostrophes, and periods'
  );

// Organization name validation schema
export const organizationNameSchema = z
  .string()
  .min(2, 'Organization name must be at least 2 characters')
  .max(100, 'Organization name must not exceed 100 characters')
  .refine(
    (name) => {
      // Allow letters, numbers, spaces, hyphens, apostrophes, periods, ampersands, and parentheses
      const orgPattern = /^[a-zA-Z0-9\s\-'.&()]+$/;
      return orgPattern.test(name.trim());
    },
    'Organization name contains invalid characters'
  );

// Auth forms validation schemas
export const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

export const signUpSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: displayNameSchema,
  organizationName: organizationNameSchema.optional(),
});

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const inviteUserSchema = z.object({
  email: emailSchema,
  displayName: displayNameSchema.optional(),
  role: z.enum(['owner', 'admin', 'read_only', 'service_account']),
});

// Password strength checker
export const checkPasswordStrength = (password: string): {
  score: number;
  feedback: string[];
  isValid: boolean;
} => {
  const feedback: string[] = [];
  let score = 0;

  if (password.length >= 8) score += 1;
  else feedback.push('At least 8 characters');

  if (/[a-z]/.test(password)) score += 1;
  else feedback.push('Include lowercase letters');

  if (/[0-9]/.test(password)) score += 1;
  else feedback.push('Include numbers');

  if (/[^a-zA-Z0-9]/.test(password)) score += 1;
  else feedback.push('Include special characters');

  const isValid = score >= 4 && password.length >= 8;

  return {
    score: Math.max(0, Math.min(4, score)),
    feedback,
    isValid,
  };
};

// Server-side validation functions
export const validateWithServer = {
  async password(password: string): Promise<{ isValid: boolean; message?: string }> {
    // Use client-side validation for now until server functions are available
    try {
      passwordSchema.parse(password);
      const strength = checkPasswordStrength(password);
      return { 
        isValid: strength.isValid, 
        message: strength.isValid ? undefined : 'Password does not meet security requirements' 
      };
    } catch (error) {
      if (error instanceof z.ZodError) {
        return { isValid: false, message: error.issues[0]?.message || 'Invalid password' };
      }
      return { isValid: false, message: 'Validation error occurred' };
    }
  },

  async email(email: string): Promise<{ isValid: boolean; message?: string }> {
    try {
      emailSchema.parse(email);
      return { isValid: true };
    } catch (error) {
      if (error instanceof z.ZodError) {
        return { isValid: false, message: error.issues[0]?.message || 'Invalid email' };
      }
      return { isValid: false, message: 'Validation error occurred' };
    }
  },

  async organizationName(name: string): Promise<{ isValid: boolean; message?: string }> {
    try {
      organizationNameSchema.parse(name);
      return { isValid: true };
    } catch (error) {
      if (error instanceof z.ZodError) {
        return { isValid: false, message: error.issues[0]?.message || 'Invalid organization name' };
      }
      return { isValid: false, message: 'Validation error occurred' };
    }
  },

  async displayName(name: string): Promise<{ isValid: boolean; message?: string }> {
    try {
      displayNameSchema.parse(name);
      return { isValid: true };
    } catch (error) {
      if (error instanceof z.ZodError) {
        return { isValid: false, message: error.issues[0]?.message || 'Invalid display name' };
      }
      return { isValid: false, message: 'Validation error occurred' };
    }
  },
};

// Rate limiting check - client-side implementation for now
export const checkRateLimit = async (
  ipAddress: string,
  formType: string,
  limitCount: number = 5,
  windowMinutes: number = 15
): Promise<{ allowed: boolean; message?: string }> => {
  try {
    // Simple client-side rate limiting using localStorage
    const key = `rate_limit_${formType}`;
    const now = Date.now();
    const windowMs = windowMinutes * 60 * 1000;
    
    const stored = localStorage.getItem(key);
    let attempts = stored ? JSON.parse(stored) : [];
    
    // Filter out old attempts
    attempts = attempts.filter((timestamp: number) => now - timestamp < windowMs);
    
    if (attempts.length >= limitCount) {
      return {
        allowed: false,
        message: `Too many attempts. Please try again later.`,
      };
    }

    // Add current attempt
    attempts.push(now);
    localStorage.setItem(key, JSON.stringify(attempts));
    
    return { allowed: true };
  } catch (error) {
    console.error('Rate limit check error:', error);
    // Allow by default if check fails
    return { allowed: true };
  }
};

// Log form submission - client-side logging for now
export const logFormSubmission = async (
  ipAddress: string,
  formType: string,
  success: boolean,
  userAgent?: string
): Promise<void> => {
  try {
    // Log to console for development
    console.log('Form submission:', { 
      formType, 
      success, 
      timestamp: new Date().toISOString(),
      userAgent: userAgent || navigator.userAgent
    });
  } catch (error) {
    console.error('Form submission logging error:', error);
  }
};